import React, { useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useSnackbar } from "notistack";
import axios from "../../api/axios";
import PublicPageLayout from "./PublicPageLayout";
import PublicToolPassportModal from "./PublicToolPassportModal";

// Inline styles for tool cards
const styles = {
    toolCard: {
        transition: 'transform 0.2s ease, box-shadow 0.2s ease',
        border: '1px solid #e0e0e0',
        borderRadius: '8px',
        overflow: 'hidden'
    },
    toolCardHover: {
        transform: 'translateY(-4px)',
        boxShadow: '0 8px 25px rgba(17, 45, 78, 0.15)'
    },
    logoContainer: {
        height: '140px',
        backgroundColor: '#ffffff',
        padding: '20px',
        borderBottom: '1px solid #f0f0f0',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
    },
    toolLogo: {
        maxWidth: '100%',
        maxHeight: '100%',
        objectFit: 'contain'
    },
    cardBody: {
        padding: '16px',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        minHeight: '140px'
    },
    toolName: {
        fontSize: '0.95rem',
        fontWeight: 600,
        color: '#112d4e',
        marginBottom: '8px',
        textAlign: 'center',
        lineHeight: 1.3
    },
    badgeContainer: {
        marginBottom: '8px'
    },
    categoryBadge: {
        backgroundColor: '#112d4e',
        fontSize: '0.7rem',
        padding: '4px 8px',
        borderRadius: '4px'
    },
    typeBadge: {
        backgroundColor: '#6c757d',
        fontSize: '0.7rem',
        padding: '4px 8px',
        borderRadius: '4px'
    },
    viewButton: {
        backgroundColor: '#112d4e',
        color: '#fff',
        border: 'none',
        padding: '8px 16px',
        borderRadius: '6px',
        fontSize: '0.85rem',
        marginTop: 'auto',
        width: '100%',
        transition: 'background-color 0.2s ease'
    }
};

/**
 * Public Tools Page
 * Displays tools for a given subcategory
 * Uses PublicPageLayout for consistent header/footer
 */
function PublicHomeTools() {
    const { subcategoryId, categoryId, panelId } = useParams();
    const { t } = useTranslation();
    const { enqueueSnackbar } = useSnackbar();
    const navigate = useNavigate();
    const [tools, setTools] = useState([]);
    const [loading, setLoading] = useState(true);
    const [subcategoryName, setSubcategoryName] = useState("");
    const [categoryName, setCategoryName] = useState("");
    const [hoveredCard, setHoveredCard] = useState(null);

    // Modal state
    const [openModal, setOpenModal] = useState(false);
    const [selectedTool, setSelectedTool] = useState(null);
    const [loadingModal, setLoadingModal] = useState(false);

    useEffect(() => {
        if (!subcategoryId) return;
        const loadTools = async () => {
            setLoading(true);
            try {
                const res = await axios.get(`/ToolPassports/Public/BySubcategory/${subcategoryId}`);
                if (typeof res.data === "string" && res.data === "No records") {
                    setTools([]);
                } else {
                    setTools(res.data);
                    if (res.data.length > 0) {
                        setSubcategoryName(res.data[0].subcategoryName);
                        setCategoryName(res.data[0].categoryName);
                    }
                }
            } catch (err) {
                enqueueSnackbar(t("cannotLoadTools") || "Cannot load tools", { variant: "error" });
            } finally {
                setLoading(false);
            }
        };
        loadTools();
    }, [subcategoryId, enqueueSnackbar, t]);

    const handleOpenModal = async (id) => {
        setLoadingModal(true);
        try {
            const res = await axios.get(`/ToolPassports/Public/${id}`);
            setSelectedTool(res.data);
            setOpenModal(true);
        } catch (err) {
            enqueueSnackbar(t("cannotLoadToolDetails") || "Cannot load tool details", { variant: "error" });
        } finally {
            setLoadingModal(false);
        }
    };

    const handleCloseModal = () => {
        setOpenModal(false);
        setSelectedTool(null);
    };

    const handleToolSelection = () => {
        navigate(`/ToolSelection?panel=${panelId}&category=${categoryId}&subcategory=${subcategoryId}`);
    };

    return (
        <PublicPageLayout>
            <div className="container px-3">
                {/* Breadcrumb */}
            <nav aria-label="breadcrumb" className="mb-3">
                <ol className="breadcrumb">
                    <li className="breadcrumb-item">
                        <Link to="/Home" style={{ color: '#112d4e' }}>
                            <i className="bi bi-house-door me-1"></i>
                            {t("home")}
                        </Link>
                    </li>
                    <li className="breadcrumb-item">
                        <Link to={`/Tools/${panelId}/${categoryId}`} style={{ color: '#112d4e' }}>
                            {categoryName || t("subcategories")}
                        </Link>
                    </li>
                    <li className="breadcrumb-item active" aria-current="page">
                        {subcategoryName || t("tools")}
                    </li>
                </ol>
            </nav>

            {/* Header */}
            <div className="card mb-4 border-0 shadow-sm" style={{ borderRadius: '12px', overflow: 'hidden' }}>
                <div
                    className="card-header"
                    style={{
                        backgroundColor: '#112d4e',
                        color: '#fff',
                        padding: '28px 20px',
                        textAlign: 'center'
                    }}
                >
                    <h4 className="mb-0" style={{ fontWeight: 600 }}>{subcategoryName || t("tools")}</h4>
                    {categoryName && (
                        <p className="mb-0 mt-2" style={{ opacity: 0.85, fontSize: '0.95rem' }}>
                            {t("category")}: {categoryName}
                        </p>
                    )}
                    {tools.length > 0 && (
                        <button
                            className="btn mt-3"
                            style={{
                                backgroundColor: 'transparent',
                                color: '#fff',
                                border: '2px solid #fff',
                                padding: '10px 24px',
                                borderRadius: '8px',
                                fontWeight: 500,
                                transition: 'all 0.2s ease'
                            }}
                            onClick={handleToolSelection}
                            onMouseEnter={(e) => {
                                e.target.style.backgroundColor = '#fff';
                                e.target.style.color = '#112d4e';
                            }}
                            onMouseLeave={(e) => {
                                e.target.style.backgroundColor = 'transparent';
                                e.target.style.color = '#fff';
                            }}
                        >
                            <i className="bi bi-sliders me-2"></i>
                            {t("compareTools")} ({tools.length} {t("tools")})
                        </button>
                    )}
                </div>
            </div>

            {/* Content */}
            {loading ? (
                <div className="text-center py-5">
                    <div className="spinner-border" role="status" style={{ color: '#112d4e' }}>
                        <span className="visually-hidden">Loading...</span>
                    </div>
                    <p className="mt-3" style={{ color: '#112d4e' }}>{t("loading")}...</p>
                </div>
            ) : tools.length === 0 ? (
                <div className="text-center py-5">
                    <p className="text-muted">{t("noToolsFound")}</p>
                    <button
                        className="btn mt-2"
                        style={{ backgroundColor: '#112d4e', color: '#fff' }}
                        onClick={() => navigate(`/Tools/${panelId}/${categoryId}`)}
                    >
                        {t("backToSubcategories")}
                    </button>
                </div>
            ) : (
                <div className="row g-4">
                    {tools.map((tool) => (
                        <div key={tool.id} className="col-12 col-sm-6 col-md-4 col-lg-3">
                            <div
                                className="card h-100"
                                style={{
                                    ...styles.toolCard,
                                    ...(hoveredCard === tool.id ? styles.toolCardHover : {})
                                }}
                                onMouseEnter={() => setHoveredCard(tool.id)}
                                onMouseLeave={() => setHoveredCard(null)}
                            >
                                {/* Tool Logo */}
                                <div style={styles.logoContainer}>
                                    <img
                                        src={tool.logo
                                            ? `${process.env.REACT_APP_SERVER_URLForLogo}/${tool.logo}`
                                            : `/assets/brand/default_img.png`}
                                        alt={tool.name}
                                        style={styles.toolLogo}
                                        onError={(e) => {
                                            e.target.onerror = null;
                                            e.target.src = '/assets/brand/default_img.png';
                                        }}
                                    />
                                </div>

                                {/* Tool Info */}
                                <div style={styles.cardBody}>
                                    <h6 style={styles.toolName}>{tool.name}</h6>

                                    {/* Show category badge */}
                                    <div style={styles.badgeContainer}>
                                        <span className="badge" style={styles.categoryBadge}>
                                            {tool.categoryName}
                                        </span>
                                    </div>

                                    {/* Show tool type if available */}
                                    {tool.typeOfToolName && (
                                        <div style={{ marginBottom: '12px' }}>
                                            <span className="badge" style={styles.typeBadge}>
                                                {tool.typeOfToolName}
                                            </span>
                                        </div>
                                    )}

                                    <button
                                        className="btn"
                                        style={styles.viewButton}
                                        onClick={() => handleOpenModal(tool.id)}
                                        disabled={loadingModal}
                                        onMouseEnter={(e) => e.target.style.backgroundColor = '#1a4a7a'}
                                        onMouseLeave={(e) => e.target.style.backgroundColor = '#112d4e'}
                                    >
                                        <i className="bi bi-eye me-2"></i>
                                        {t("viewToolPassport")}
                                    </button>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {/* Navigation Buttons */}
            <div className="d-flex justify-content-center gap-3 mt-5 mb-3">
                <button
                    className="btn"
                    onClick={() => navigate(`/Tools/${panelId}/${categoryId}`)}
                    style={{
                        borderColor: '#112d4e',
                        color: '#112d4e',
                        border: '2px solid #112d4e',
                        padding: '10px 20px',
                        borderRadius: '8px',
                        fontWeight: 500,
                        transition: 'all 0.2s ease'
                    }}
                    onMouseEnter={(e) => {
                        e.target.style.backgroundColor = '#112d4e';
                        e.target.style.color = '#fff';
                    }}
                    onMouseLeave={(e) => {
                        e.target.style.backgroundColor = 'transparent';
                        e.target.style.color = '#112d4e';
                    }}
                >
                    <i className="bi bi-arrow-left me-2"></i>
                    {t("backToSubcategories")}
                </button>
                <button
                    className="btn"
                    onClick={() => navigate('/Home')}
                    style={{
                        borderColor: '#112d4e',
                        color: '#112d4e',
                        border: '2px solid #112d4e',
                        padding: '10px 20px',
                        borderRadius: '8px',
                        fontWeight: 500,
                        transition: 'all 0.2s ease'
                    }}
                    onMouseEnter={(e) => {
                        e.target.style.backgroundColor = '#112d4e';
                        e.target.style.color = '#fff';
                    }}
                    onMouseLeave={(e) => {
                        e.target.style.backgroundColor = 'transparent';
                        e.target.style.color = '#112d4e';
                    }}
                >
                    <i className="bi bi-house-door me-2"></i>
                    {t("backToHome")}
                </button>
            </div>

            {/* Tool Passport Modal */}
            <PublicToolPassportModal
                open={openModal}
                onClose={handleCloseModal}
                toolPassport={selectedTool}
            />
            </div>
        </PublicPageLayout>
    );
}

export default PublicHomeTools;
